import { h64 } from 'xxhashjs';

import {
  type CustomExpressionAxis,
  isCompletionFormula,
  parseCustomAxis,
} from '@neptune/shared/custom-expressions-domain';
import { WidgetSourceType } from '@neptune/shared/widgets-domain';

import {
  type CompiledFloatExpression,
  type CompiledFloatSeriesY,
  type FloatSeriesCompileResult,
  type FloatSeriesConfigurationIssue,
  type FloatSeriesRange,
  type FloatSeriesSelector,
  type FloatSeriesX,
  type FloatSeriesY,
  type VegaLiteFloatSeriesOptionsV2,
  type VegaLiteFloatSeriesQuery,
} from './vega-lite-float-series-types';
import {
  cloneJsonObject,
  escapeJsonPointerSegment,
  isJsonObject,
  utf8ByteLength,
} from './vega-lite-json';
import validationMetadata from './vega-lite-validation-metadata.json';
import {
  VEGA_LITE_FLOAT_SERIES_LIMITS,
  type VegaLiteFloatSeriesLimits,
} from './vega-lite-widget-limits';

const RESERVED_FIELDS = new Set(validationMetadata.floatSeries.reservedFields);

export function compareVegaLiteFloatKeys(left: string, right: string): number {
  if (left === right) {
    return 0;
  }

  return left < right ? -1 : 1;
}

export function getVegaLiteFloatXAxisLabel(x: FloatSeriesX): string {
  switch (x.kind) {
    case 'attribute':
      return x.alias || x.attribute.name;
    case 'expression':
      return x.alias || x.expression;
    case 'step':
      return 'Step';
    case 'relativeTime':
      return 'Relative time (ms)';
    case 'absoluteTime':
      return 'Absolute time (ms)';
  }
}

function object(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function exactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
  path: string,
  issues: FloatSeriesConfigurationIssue[],
) {
  Object.keys(value)
    .filter((key) => !keys.includes(key))
    .forEach((key) =>
      issues.push({
        path: `${path}/${escapeJsonPointerSegment(key)}`,
        message: `Unsupported property "${key}".`,
      }),
    );
}

function readSelector(
  value: Record<string, unknown>,
  path: string,
  issues: FloatSeriesConfigurationIssue[],
): FloatSeriesSelector | undefined {
  if (value.kind === 'attribute') {
    const attribute = value.attribute;

    if (object(attribute)) {
      exactKeys(attribute, ['name', 'type'], `${path}/attribute`, issues);

      if (attribute.type === 'floatSeries' && typeof attribute.name === 'string') {
        return { kind: 'attribute', attribute: { name: attribute.name, type: 'floatSeries' } };
      }
    }

    issues.push({
      path: `${path}/attribute`,
      message: 'Use an exact {name, type: "floatSeries"} attribute.',
    });
  } else if (value.kind === 'expression' && typeof value.expression === 'string') {
    return { kind: 'expression', expression: value.expression };
  } else {
    issues.push({
      path,
      message: 'Use kind "attribute" with an attribute or "expression" with expression text.',
    });
  }

  return undefined;
}

function readAlias(
  value: Record<string, unknown>,
  path: string,
  issues: FloatSeriesConfigurationIssue[],
): { alias?: string } {
  if (value.alias === undefined) {
    return {};
  }

  if (typeof value.alias !== 'string') {
    issues.push({ path: `${path}/alias`, message: 'The display alias must be a string.' });
    return {};
  }

  return { alias: value.alias };
}

function readX(value: unknown, issues: FloatSeriesConfigurationIssue[]): FloatSeriesX | undefined {
  if (!object(value)) {
    issues.push({ path: '/query/x', message: 'Select an X definition.' });
    return undefined;
  }

  if (value.kind === 'step' || value.kind === 'relativeTime' || value.kind === 'absoluteTime') {
    exactKeys(value, ['kind'], '/query/x', issues);
    return { kind: value.kind };
  }

  exactKeys(
    value,
    ['kind', value.kind === 'attribute' ? 'attribute' : 'expression', 'alias'],
    '/query/x',
    issues,
  );
  const alias = readAlias(value, '/query/x', issues);
  const selector = readSelector(value, '/query/x', issues);
  return selector && { ...selector, ...alias };
}

function readY(
  value: unknown,
  issues: FloatSeriesConfigurationIssue[],
): FloatSeriesY[] | undefined {
  if (!Array.isArray(value)) {
    issues.push({ path: '/query/y', message: 'Y must be an ordered array of definitions.' });
    return undefined;
  }

  const result: FloatSeriesY[] = [];
  value.forEach((entry: unknown, index) => {
    const path = `/query/y/${index}`;

    if (!object(entry)) {
      issues.push({ path, message: 'Every Y definition must be an object.' });
      return;
    }

    exactKeys(
      entry,
      ['kind', entry.kind === 'attribute' ? 'attribute' : 'expression', 'as', 'alias'],
      path,
      issues,
    );
    const alias = readAlias(entry, path, issues);
    const selector = readSelector(entry, path, issues);

    if (typeof entry.as !== 'string') {
      issues.push({ path: `${path}/as`, message: 'Set a stable output field binding "as".' });
    } else if (selector) {
      result.push({ ...selector, ...alias, as: entry.as });
    }
  });
  return result;
}

function readRange(
  value: unknown,
  path: string,
  issues: FloatSeriesConfigurationIssue[],
): FloatSeriesRange | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (!object(value)) {
    issues.push({
      path,
      message: 'A range must be an object with optional finite "from" and "to".',
    });
    return undefined;
  }

  exactKeys(value, ['from', 'to'], path, issues);
  const range: FloatSeriesRange = {};
  (['from', 'to'] as const).forEach((key) => {
    if (value[key] === undefined) {
      return;
    }

    if (typeof value[key] !== 'number' || !Number.isFinite(value[key])) {
      issues.push({ path: `${path}/${key}`, message: 'Range boundaries must be finite numbers.' });
    } else {
      range[key] = value[key];
    }
  });

  if (range.from !== undefined && range.to !== undefined && range.from > range.to) {
    issues.push({ path, message: 'Range "from" must not exceed "to".' });
  }

  return range;
}

export function decodeVegaLiteFloatSeriesOptions(
  value: unknown,
):
  | { status: 'valid'; options: VegaLiteFloatSeriesOptionsV2 }
  | { status: 'invalid'; issues: FloatSeriesConfigurationIssue[] } {
  const issues: FloatSeriesConfigurationIssue[] = [];

  if (!object(value)) {
    return { status: 'invalid', issues: [{ path: '/', message: 'Options must be an object.' }] };
  }

  exactKeys(value, ['version', 'query', 'dataFormat', 'groupBy', 'spec'], '', issues);

  if (value.version !== 2) {
    issues.push({ path: '/version', message: 'Float series require options version 2.' });
  }

  const query = value.query;
  let x: FloatSeriesX | undefined;
  let y: FloatSeriesY[] | undefined;
  const view: VegaLiteFloatSeriesQuery['view'] = {};

  if (!object(query)) {
    issues.push({ path: '/query', message: 'Set a floatSeries query object.' });
  } else {
    exactKeys(query, ['kind', 'x', 'y', 'view'], '/query', issues);

    if (query.kind !== 'floatSeries') {
      issues.push({ path: '/query/kind', message: 'Version 2 requires query.kind "floatSeries".' });
    }

    x = readX(query.x, issues);
    y = readY(query.y, issues);

    if (!object(query.view)) {
      issues.push({
        path: '/query/view',
        message: 'Set view to an object; use {} for the full range.',
      });
    } else {
      const queryView = query.view;
      exactKeys(queryView, ['xRange', 'stepRange'], '/query/view', issues);
      (['xRange', 'stepRange'] as const).forEach((key) => {
        const range = readRange(queryView[key], `/query/view/${key}`, issues);

        if (range !== undefined) {
          view[key] = range;
        }
      });
    }
  }

  if (value.dataFormat !== 'wide' && value.dataFormat !== 'long') {
    issues.push({ path: '/dataFormat', message: 'Data format must be "wide" or "long".' });
  }

  if (!Array.isArray(value.groupBy) || value.groupBy.length !== 0) {
    issues.push({
      path: '/groupBy',
      message: 'Float series require groupBy: []; evaluation grouping is not supported.',
    });
  }

  if (!isJsonObject(value.spec)) {
    issues.push({ path: '/spec', message: 'The Vega-Lite definition must be a JSON object.' });
  }

  if (
    issues.length ||
    !x ||
    !y ||
    !isJsonObject(value.spec) ||
    (value.dataFormat !== 'wide' && value.dataFormat !== 'long')
  ) {
    return { status: 'invalid', issues };
  }

  return {
    status: 'valid',
    options: {
      version: 2,
      query: { kind: 'floatSeries', x, y, view },
      dataFormat: value.dataFormat,
      groupBy: [],
      spec: cloneJsonObject(value.spec),
    },
  };
}

function compileSelector(
  selector: FloatSeriesSelector,
  path: string,
  issues: FloatSeriesConfigurationIssue[],
  axis: CustomExpressionAxis,
  limits: VegaLiteFloatSeriesLimits,
): CompiledFloatExpression | undefined {
  if (selector.kind === 'attribute') {
    if (!selector.attribute.name.trim()) {
      issues.push({
        path: `${path}/attribute/name`,
        message: 'An exact attribute path is required.',
      });
      return undefined;
    }

    return { formula: '_0', attributes: [selector.attribute.name] };
  }

  if (utf8ByteLength(selector.expression) > limits.maxExpressionBytes) {
    issues.push({
      path: `${path}/expression`,
      message: `Expressions must not exceed ${limits.maxExpressionBytes} UTF-8 bytes.`,
    });
    return undefined;
  }

  // Custom X shares the chart grammar: it may also read `step` and run attributes via `attr(...)`.
  const parsed = parseCustomAxis(selector.expression, axis);

  if (parsed.status !== 'valid') {
    issues.push({
      path: `${path}/expression`,
      message: parsed.error?.message ?? 'Invalid custom expression.',
    });
    return undefined;
  }

  const runAttributes = parsed.runAttributes ?? [];
  const runAttributeDefaults = parsed.runAttributeDefaults ?? [];

  if (parsed.attributes.length + runAttributes.length > limits.maxExpressionInputs) {
    issues.push({
      path: `${path}/expression`,
      message: `An expression may read at most ${limits.maxExpressionInputs} distinct attributes.`,
    });
    return undefined;
  }

  if ([...parsed.attributes, ...runAttributes].some((name) => !name.trim())) {
    issues.push({
      path: `${path}/expression`,
      message: 'Every expression input requires a non-blank exact attribute path.',
    });
    return undefined;
  }

  return {
    formula: parsed.formula,
    attributes: [...parsed.attributes],
    ...(runAttributes.length > 0 ? { runAttributes: [...runAttributes] } : {}),
    ...(runAttributeDefaults.some((value) => value !== null)
      ? { runAttributeDefaults: [...runAttributeDefaults] }
      : {}),
  };
}

export function compileVegaLiteFloatSeriesQuery(
  query: VegaLiteFloatSeriesQuery,
  limits: VegaLiteFloatSeriesLimits = VEGA_LITE_FLOAT_SERIES_LIMITS,
): FloatSeriesCompileResult {
  const issues: FloatSeriesConfigurationIssue[] = [];

  if (query.y.length < 1 || query.y.length > limits.maxY) {
    issues.push({
      path: '/query/y',
      message: `Select between 1 and ${limits.maxY} Y definitions.`,
    });
  }

  const bindings = new Set<string>();
  const signatures = new Set<string>();
  const hashes = new Map<string, string>();
  const y: CompiledFloatSeriesY[] = [];
  query.y.forEach((definition, index) => {
    const path = `/query/y/${index}`;

    if (
      !/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(definition.as) ||
      definition.as.includes(validationMetadata.floatSeries.reservedBindingSubstring) ||
      RESERVED_FIELDS.has(definition.as)
    ) {
      issues.push({
        path: `${path}/as`,
        message: `"${definition.as}" is not an available field binding. Use 1-64 letters, digits or underscores, starting with a letter; fixed fields and "__" are reserved.`,
      });
    }

    if (bindings.has(definition.as)) {
      issues.push({ path: `${path}/as`, message: `Duplicate output binding "${definition.as}".` });
    }

    bindings.add(definition.as);
    const expression = compileSelector(definition, path, issues, 'y', limits);

    if (!expression) {
      return;
    }

    const signature = JSON.stringify(
      definition.kind === 'attribute'
        ? ['floatSeries', 'attribute', definition.attribute.name]
        : ['floatSeries', 'expression', expression.formula, expression.attributes],
    );

    if (signatures.has(signature)) {
      issues.push({
        path,
        message:
          'Duplicate Y definition; each raw path or normalized expression may be selected only once.',
      });
    }

    signatures.add(signature);
    const metricKey = `m2:${h64(signature, 0x12345612).toString(36)}`;

    if (hashes.has(metricKey) && hashes.get(metricKey) !== signature) {
      issues.push({ path, message: 'Metric identity collision; use a distinct definition.' });
    }

    hashes.set(metricKey, signature);
    y.push({
      as: definition.as,
      ...(definition.alias === undefined ? {} : { alias: definition.alias }),
      kind: definition.kind,
      name: definition.kind === 'attribute' ? definition.attribute.name : definition.expression,
      metricKey,
      signature,
      expression,
      includePreview: definition.kind === 'attribute' || isCompletionFormula(expression),
    });
  });
  const xExpression =
    query.x.kind === 'attribute' || query.x.kind === 'expression'
      ? compileSelector(query.x, '/query/x', issues, 'x', limits)
      : undefined;
  // Run attributes are scalar values of the run, not float-series dependencies.
  const xDependencies = xExpression?.attributes ?? [];
  const paths = Array.from(
    new Set([...y.flatMap((entry) => entry.expression.attributes), ...xDependencies]),
  ).sort();

  if (paths.length > limits.maxDependencies) {
    issues.push({
      path: '/query',
      message: `A float-series widget may read at most ${limits.maxDependencies} distinct dependency paths.`,
    });
  }

  readRange(query.view.xRange, '/query/view/xRange', issues);
  readRange(query.view.stepRange, '/query/view/stepRange', issues);

  if (issues.length) {
    return { status: 'invalid', issues };
  }

  return {
    status: 'valid',
    query: {
      x: query.x,
      ...(xExpression === undefined ? {} : { xExpression }),
      xDependencies,
      y,
      sources: paths.map((value) => ({
        type: WidgetSourceType.ATTRIBUTE,
        value,
        metadata: { attributeType: 'floatSeries' },
      })),
      view: query.view,
    },
  };
}

export function getVegaLiteFloatSeriesManifestIssues(
  options: VegaLiteFloatSeriesOptionsV2,
  sources: readonly unknown[],
): FloatSeriesConfigurationIssue[] {
  const compiled = compileVegaLiteFloatSeriesQuery(options.query);

  if (compiled.status === 'invalid') {
    return compiled.issues;
  }

  const exact =
    sources.length === compiled.query.sources.length &&
    sources.every(
      (source, index) =>
        object(source) &&
        source.type === WidgetSourceType.ATTRIBUTE &&
        source.value === compiled.query.sources[index].value &&
        object(source.metadata) &&
        source.metadata.attributeType === 'floatSeries' &&
        Object.keys(source).every((key) => ['type', 'value', 'metadata'].includes(key)) &&
        Object.entries(source.metadata).every(
          ([key, value]) =>
            key === 'attributeType' || (key === 'subproperty' && value === undefined),
        ),
    );
  return exact
    ? []
    : [
        {
          path: '/sources',
          message:
            'Float-series sources must exactly equal the sorted, deduplicated dependency manifest derived from query.x and query.y.',
        },
      ];
}
